import type { DatasetStorageBackend } from "@/api/datasetStorageBackend";
import { IconButton, Scrollbar, Text } from "@carbon6/c6-frontend-design";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { type DatasetFile, type DatasetFileKind, type DirectoryFolder } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import {
  VideoCameraIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FileIcon,
  FolderOutlineIcon,
  RefreshIcon,
} from "@/components/icons";
import { createLogger } from "@/logging";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { resolveLoadErrorMessage, type LoadState } from "../loadState";
import { TreeRow } from "../TreeRow";
import { withSharedEpisodePages, type FileBrowserSource } from "../fileBrowserSource";
import { ExtraFileUploadAction } from "./ExtraFileUploadAction";
import {
  classifyFolder,
  directFileCounts,
  folderChannelName,
  type FolderKind,
} from "./folderClassify";
import type { FolderSelection } from "./selection";
import { useDirectoryTree } from "./useDirectoryTree";
import { useEpisodePage } from "../useEpisodePage";
import styles from "./FileBrowser.module.css";

const logger = createLogger("components/dataset/fileBrowser/files");
const FILE_PAGE_SIZE = 50;

// Files 모드 좌측. canonical 채널과 extra 폴더를 병합한 디렉터리 트리다. 폴더를 펼치면 종류에
// 따라 하위 폴더 또는 에피소드 page-group 을 보인다. **종류와 무관하게 직속 파일이 있으면 그
// 목록도 함께** 그 자리에 보여 준다.
export function FilesSidebar({
  source,
  datasetUuid,
  selection,
  onSelect,
  canModify,
  storageBackend,
  writeNonce,
  onChanged,
}: {
  source: FileBrowserSource;
  datasetUuid: string;
  selection: FolderSelection;
  onSelect: (selection: FolderSelection) => void;
  canModify: boolean;
  // 이 Dataset 의 저장 위치. 추가 파일 업로드 창이 위치마다 다른 PUT 계약(크기 상한, 이미 있는
  // 경로의 거절)을 안내하는 데 쓴다. 모르면 생략한다.
  storageBackend?: DatasetStorageBackend;
  // write(업로드/이름변경/삭제) 후 부모가 올리는 nonce. 트리와 열린 파일 목록을 다시 조회하게 한다.
  writeNonce: number;
  onChanged: () => void;
}): ReactNode {
  const { state: tree, reload: reloadTree } = useDirectoryTree(source, datasetUuid);
  // 채널 폴더는 폴더마다 같은 에피소드 page 를 보인다. 트리 아래의 폴더가 응답을 함께 쓴다.
  // 이 트리가 떠 있는 동안만 보관한다. 모드를 바꾸거나 창을 닫으면 다시 받는다.
  const treeSource = useMemo(() => withSharedEpisodePages(source), [source]);

  // write 후(nonce 변경) 디렉터리 트리를 다시 조회한다(폴더별 canonical/extra 카운트 갱신).
  useEffect(() => {
    if (writeNonce > 0) reloadTree();
  }, [writeNonce, reloadTree]);

  const rootFileCount =
    tree.status === "success"
      ? tree.data.root_canonical_file_count + tree.data.root_extra_file_count
      : 0;

  return (
    <Scrollbar className={styles.tree} aria-label="파일">
      {/* 스크롤 컨테이너(.tree)와 세로 스택 레이아웃(.treeInner)을 분리한다. flex 컨테이너에 직접
          자식을 두면 폴더를 여럿 펼쳤을 때 자식이 shrink 되어 스크롤 대신 압축된다. */}
      <div className={styles.treeInner}>
        {tree.status === "error" ? (
          <EmptyState
            tone="error"
            action={{ label: "다시 시도", onClick: reloadTree, icon: <RefreshIcon /> }}
          >
            {tree.message}
          </EmptyState>
        ) : null}
        {tree.status === "success" ? (
          tree.data.folders.length === 0 && rootFileCount === 0 ? (
            <EmptyState>표시할 파일이 없습니다.</EmptyState>
          ) : (
            <>
              {tree.data.folders.map((folder) => (
                <FolderNode
                  key={folder.path}
                  folder={folder}
                  depth={0}
                  source={treeSource}
                  datasetUuid={datasetUuid}
                  selection={selection}
                  onSelect={onSelect}
                  canModify={canModify}
                  storageBackend={storageBackend}
                  writeNonce={writeNonce}
                  onChanged={onChanged}
                />
              ))}
              {rootFileCount > 0 ? (
                <FolderNode
                  key="__root_files"
                  folder={{
                    name: "(루트 파일)",
                    path: "",
                    canonical_file_count: tree.data.root_canonical_file_count,
                    extra_file_count: tree.data.root_extra_file_count,
                    // 루트 직속 파일을 담는 합성 노드라 canonical 분류가 없다.
                    namespace: null,
                    channel: null,
                    folders: [],
                  }}
                  depth={0}
                  source={treeSource}
                  datasetUuid={datasetUuid}
                  selection={selection}
                  onSelect={onSelect}
                  canModify={canModify}
                  storageBackend={storageBackend}
                  writeNonce={writeNonce}
                  onChanged={onChanged}
                />
              ) : null}
            </>
          )
        ) : null}
      </div>
    </Scrollbar>
  );
}

function FolderNode({
  folder,
  depth,
  source,
  datasetUuid,
  selection,
  onSelect,
  canModify,
  storageBackend,
  writeNonce,
  onChanged,
}: {
  folder: DirectoryFolder;
  depth: number;
  source: FileBrowserSource;
  datasetUuid: string;
  selection: FolderSelection;
  onSelect: (selection: FolderSelection) => void;
  canModify: boolean;
  storageBackend: DatasetStorageBackend | undefined;
  writeNonce: number;
  onChanged: () => void;
}): ReactNode {
  const kind = classifyFolder(folder);
  const [expanded, setExpanded] = useState(false);
  const count = folder.canonical_file_count + folder.extra_file_count;
  const direct = directFileCounts(folder);
  // 채널 폴더는 에피소드 목록이 canonical 파일을 대신하므로 직속 목록에는 extra 만 담는다.
  const listedKind: DatasetFileKind | undefined = isChannelKind(kind) ? "extra" : undefined;
  const hasDirectFiles =
    listedKind === "extra" ? direct.extra > 0 : direct.canonical + direct.extra > 0;
  // 추가 파일은 어느 폴더에든 올릴 수 있다. 원본 보호는 폴더 단위가 아니라 경로 단위다(서버 409 +
  // 다이얼로그의 이름 probe).
  const canUpload = canModify && source.declare !== undefined;

  return (
    <>
      {/* 행 본문 전체가 펼침 토글이다(`expand`). 그래서 이름 위에 텍스트 커서가 보이고 눌리지
          않는 문제가 없다. 업로드 버튼은 본문 바깥 형제라서 버튼 중첩이 생기지 않는다. */}
      <TreeRow
        mode="expand"
        label={folder.name}
        title={folder.path || folder.name}
        depth={depth}
        icon={kind === "episodeVideo" ? <VideoCameraIcon /> : <FolderOutlineIcon />}
        count={count}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((current) => !current)}
        trailing={
          canUpload ? (
            <ExtraFileUploadAction
              source={source}
              datasetUuid={datasetUuid}
              storageBackend={storageBackend}
              initialPath={folder.path}
              ariaLabel={`${folder.name} 폴더에 업로드`}
              onUploaded={onChanged}
            />
          ) : null
        }
      />

      {expanded && kind === "group"
        ? folder.folders.map((child) => (
            <FolderNode
              key={child.path}
              folder={child}
              depth={depth + 1}
              source={source}
              datasetUuid={datasetUuid}
              selection={selection}
              onSelect={onSelect}
              canModify={canModify}
              storageBackend={storageBackend}
              writeNonce={writeNonce}
              onChanged={onChanged}
            />
          ))
        : null}

      {expanded && isChannelKind(kind) ? (
        <EpisodePageGroup
          source={source}
          datasetUuid={datasetUuid}
          depth={depth + 1}
          kind={kind}
          channel={folderChannelName(folder.path)}
          selection={selection}
          onSelect={onSelect}
        />
      ) : null}

      {expanded && hasDirectFiles ? (
        <FileListGroup
          source={source}
          datasetUuid={datasetUuid}
          depth={depth + 1}
          parent={folder.path}
          kind={listedKind}
          selection={selection}
          onSelect={onSelect}
          writeNonce={writeNonce}
        />
      ) : null}
    </>
  );
}

function isChannelKind(kind: FolderKind): kind is "episodeVideo" | "episodeParquet" {
  return kind === "episodeVideo" || kind === "episodeParquet";
}

// 채널 폴더 안 에피소드 page-group(page-based). parquet 채널이면 전체 에피소드 뷰, video 채널이면
// 단일 카메라 영상으로 선택된다(channel = 폴더 마지막 세그먼트).
function EpisodePageGroup({
  source,
  datasetUuid,
  depth,
  kind,
  channel,
  selection,
  onSelect,
}: {
  source: FileBrowserSource;
  datasetUuid: string;
  depth: number;
  kind: Extract<FolderKind, "episodeVideo" | "episodeParquet">;
  channel: string;
  selection: FolderSelection;
  onSelect: (selection: FolderSelection) => void;
}): ReactNode {
  const { state, page, total, totalPages, rangeEnd, goPrev, goNext, reload } = useEpisodePage(
    source,
    datasetUuid,
  );

  return (
    <div
      className={styles.pageGroup}
      style={{ marginLeft: `calc(var(--tree-row-inset) + ${depth} * var(--tree-indent))` }}
    >
      <Scrollbar className={styles.pageGroupList} aria-label="에피소드">
        {state.status === "error" ? <RetryNotice message={state.message} onRetry={reload} /> : null}
        {state.status === "success"
          ? state.data.map((episode) => {
              const selected =
                kind === "episodeVideo"
                  ? selection?.kind === "channelVideo" &&
                    selection.episode.uuid === episode.uuid &&
                    selection.channel === channel
                  : selection?.kind === "episode" && selection.episode.uuid === episode.uuid;
              const title = episode.display_name ?? `episode_${episode.episode_index}`;
              return (
                <button
                  key={episode.uuid}
                  type="button"
                  className={styles.pageGroupRow}
                  data-selected={selected ? "true" : undefined}
                  onClick={() =>
                    onSelect(
                      kind === "episodeVideo"
                        ? { kind: "channelVideo", episode, channel }
                        : { kind: "episode", episode },
                    )
                  }
                >
                  <span className={styles.fileDot} data-kind={kind} aria-hidden="true" />
                  <span className={styles.fileName} title={title}>
                    {title}
                  </span>
                </button>
              );
            })
          : null}
      </Scrollbar>
      <div className={styles.pageGroupFooter}>
        <Text variant={hatcheryTextModes["caption-bold"]} className={styles.pageGroupLabel}>
          에피소드
        </Text>
        <div className={styles.pager}>
          <IconButton
            aria-label="이전 페이지"
            size="sm"
            variant="surface"
            disabled={page === 0}
            onClick={goPrev}
          >
            <ChevronLeftIcon />
          </IconButton>
          <span className={styles.pageLabel}>{total > 0 ? `${rangeEnd} / ${total}` : "0 / 0"}</span>
          <IconButton
            aria-label="다음 페이지"
            size="sm"
            variant="surface"
            disabled={page >= totalPages - 1}
            onClick={goNext}
          >
            <ChevronRightIcon />
          </IconButton>
        </div>
      </div>
    </div>
  );
}

// 폴더 직속 파일 목록(page-based). 서버가 카탈로그(episode·meta·extra)를 합치고 행마다 `kind`
// 로 밝힌다. 그래서 이 컴포넌트는 한 목록만 다룬다. `kind` 를 주면 그 종류만 받는다(채널 폴더의
// extra).
function FileListGroup({
  source,
  datasetUuid,
  depth,
  parent,
  kind,
  selection,
  onSelect,
  writeNonce,
}: {
  source: FileBrowserSource;
  datasetUuid: string;
  depth: number;
  parent: string;
  kind: DatasetFileKind | undefined;
  selection: FolderSelection;
  onSelect: (selection: FolderSelection) => void;
  // 부모 write nonce. 업로드/삭제/이름변경 후 이 폴더의 파일 목록을 다시 조회한다.
  writeNonce: number;
}): ReactNode {
  const [state, setState] = useState<LoadState<DatasetFile[]>>({ status: "idle" });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);
  const folderFiles = source.folderFiles;

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    void folderFiles(datasetUuid, { parent, kind, page, size: FILE_PAGE_SIZE })
      .then((resp) => {
        if (cancelled) return;
        setState({ status: "success", data: resp.items });
        setTotal(resp.total);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        logger.error("folder files failed", error);
        setState({ status: "error", message: resolveLoadErrorMessage(error) });
      });
    return () => {
      cancelled = true;
    };
    // folderFiles 는 모듈 함수(안정)다. datasetUuid+parent+kind+page+reloadToken 이 실제 키다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetUuid, parent, kind, page, reloadToken, writeNonce]);

  const totalPages = Math.max(1, Math.ceil(total / FILE_PAGE_SIZE));
  const rangeEnd = Math.min(page * FILE_PAGE_SIZE, total);
  const rows = state.status === "success" ? state.data : [];

  return (
    <div
      className={styles.pageGroup}
      style={{ marginLeft: `calc(var(--tree-row-inset) + ${depth} * var(--tree-indent))` }}
    >
      <Scrollbar className={styles.pageGroupList} aria-label="파일">
        {state.status === "error" ? (
          <RetryNotice
            message={state.message}
            onRetry={() => setReloadToken((current) => current + 1)}
          />
        ) : null}
        {state.status === "success" && rows.length === 0 ? (
          <EmptyState size="sm" mode="plain">
            표시할 파일이 없습니다.
          </EmptyState>
        ) : null}
        {rows.map((file) => (
          <button
            key={file.uuid}
            type="button"
            className={styles.pageGroupRow}
            data-selected={isFileSelected(selection, file) ? "true" : undefined}
            onClick={() => onSelect({ kind: "file", file })}
          >
            <span className={styles.fileIcon} aria-hidden="true">
              <FileIcon />
            </span>
            <span className={styles.fileName} title={file.relative_path}>
              {file.filename}
            </span>
          </button>
        ))}
      </Scrollbar>
      {total > FILE_PAGE_SIZE ? (
        <div className={styles.pageGroupFooter}>
          <Text variant={hatcheryTextModes["caption-bold"]} className={styles.pageGroupLabel}>
            파일
          </Text>
          <div className={styles.pager}>
            <IconButton
              aria-label="이전 페이지"
              size="sm"
              variant="surface"
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              <ChevronLeftIcon />
            </IconButton>
            <span className={styles.pageLabel}>{`${rangeEnd} / ${total}`}</span>
            <IconButton
              aria-label="다음 페이지"
              size="sm"
              variant="surface"
              disabled={page >= totalPages}
              onClick={() => setPage((current) => current + 1)}
            >
              <ChevronRightIcon />
            </IconButton>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// 선택 강조 판정. 카탈로그와 uuid 가 모두 같아야 한다(카탈로그가 다르면 uuid 공간도 다르다).
function isFileSelected(selection: FolderSelection, file: DatasetFile): boolean {
  return (
    selection !== null &&
    selection.kind === "file" &&
    selection.file.kind === file.kind &&
    selection.file.uuid === file.uuid
  );
}

// 로딩 실패 시 재시도 버튼. 에러 메시지는 좁은 영역에서 잘리므로 title 툴팁으로만 노출한다.
function RetryNotice({ message, onRetry }: { message: string; onRetry: () => void }): ReactNode {
  return (
    <EmptyState
      size="sm"
      mode="plain"
      tone="error"
      action={{ label: "다시 시도", onClick: onRetry, icon: <RefreshIcon /> }}
    >
      {message}
    </EmptyState>
  );
}
